import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useAdminChat } from '../../hooks/useAdminChat';
import { useAuth } from '../../contexts/AuthContext';
import { sendMessageToAdmin, markAsRead } from '../../services/adminChatService';
import { uploadImage } from '../../services/imageUploadService';
import { uploadAudio } from '../../services/audioUploadService';
import './AdminChatWidget.css';

export const AdminChatWidget: React.FC = () => {
  const { user } = useAuth();
  const { messages, metadata, loading } = useAdminChat();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const unreadCount = metadata?.unreadCountNutritionist || 0;

  // Scroll automático para última mensagem
  useEffect(() => {
    if (messagesEndRef.current && isOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Marcar como lida quando abrir o chat
  useEffect(() => {
    if (isOpen && user?.uid && unreadCount > 0) {
      markAsRead(user.uid, user.uid);
    }
  }, [isOpen, user?.uid, unreadCount]);

  const handleSend = async () => {
    if (!user?.uid || !message.trim()) return;

    try {
      await sendMessageToAdmin(user.uid, message);
      setMessage('');
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  // Função para iniciar gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);

      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      alert('Não foi possível iniciar a gravação. Verifique as permissões do microfone.');
    }
  };

  // Função para parar gravação
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Função para cancelar gravação
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  // Função para enviar áudio
  const sendAudio = async (audioBlob: Blob) => {
    if (!user?.uid) return;

    try {
      setUploadingAudio(true);
      
      const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
      const { url: audioUrl } = await uploadAudio(audioFile, user.uid);
      
      await sendMessageToAdmin(user.uid, '', undefined, audioUrl);
      
      setRecordingDuration(0);
    } catch (error: any) {
      console.error('Erro ao enviar áudio:', error);
      alert(error.message || 'Não foi possível enviar o áudio. Tente novamente.');
    } finally {
      setUploadingAudio(false);
    }
  };

  // Função para selecionar e enviar imagem
  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user?.uid) return;

    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem.');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    try {
      setUploadingImage(true);
      
      const { url: imageUrl } = await uploadImage(file, user.uid, 'admin-chat-images');
      await sendMessageToAdmin(user.uid, '', imageUrl);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Erro ao enviar imagem:', error);
      alert(error.message || 'Não foi possível enviar a imagem. Tente novamente.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Limpar ao desmontar
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  return (
    <>
      {/* Botão flutuante */}
      <button
        className={`admin-chat-widget-button ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => setIsOpen(true)}
        title="Chat com Administrador"
        data-tooltip="Chat com Administrador"
        aria-label={`Chat com Administrador${unreadCount > 0 ? ` - ${unreadCount} mensagem${unreadCount > 1 ? 's' : ''} não lida${unreadCount > 1 ? 's' : ''}` : ''}`}
      >
        <span style={{ position: 'relative', zIndex: 2 }}>💬</span>
        {unreadCount > 0 && (
          <span className="admin-chat-widget-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Modal do chat */}
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Chat com Administrador"
        footer={
          <Button variant="secondary" onClick={() => setIsOpen(false)}>
            Fechar
          </Button>
        }
      >
        <div className="admin-chat-container">
          {loading ? (
            <div className="admin-chat-loading">
              <p>Carregando conversa...</p>
            </div>
          ) : (
            <>
              <div className="admin-chat-messages">
                {messages.length === 0 ? (
                  <p className="admin-chat-empty">
                    Nenhuma mensagem ainda. Envie uma mensagem para o administrador.
                  </p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`admin-chat-message ${
                        m.author === 'nutritionist' ? 'sent' : 'received'
                      }`}
                    >
                      {m.image ? (
                        <div style={{ marginBottom: '8px' }}>
                          <img
                            src={m.image}
                            alt="Imagem enviada"
                            style={{
                              maxWidth: '300px',
                              maxHeight: '300px',
                              borderRadius: '8px',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ) : m.audio ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <audio controls style={{ maxWidth: '250px' }}>
                            <source src={m.audio} type="audio/webm" />
                            <source src={m.audio} type="audio/mpeg" />
                            <source src={m.audio} type="audio/wav" />
                            Seu navegador não suporta áudio.
                          </audio>
                          <span style={{ fontSize: '12px', opacity: 0.7 }}>🎤</span>
                        </div>
                      ) : (
                        <p>{m.text}</p>
                      )}
                      {m.createdAt && (
                        <span className="admin-chat-time">{m.createdAt}</span>
                      )}
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="admin-chat-input">
                {isRecording && (
                  <div className="admin-chat-recording">
                    <div className="admin-chat-recording-indicator" />
                    <span>
                      Gravando... {Math.floor(recordingDuration / 60)}:
                      {(recordingDuration % 60).toString().padStart(2, '0')}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={cancelRecording}
                      disabled={uploadingAudio}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      onClick={stopRecording}
                      disabled={uploadingAudio}
                    >
                      {uploadingAudio ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                )}

                {uploadingImage && !isRecording && (
                  <div className="admin-chat-uploading">
                    <span>⏳</span>
                    <span>Enviando imagem...</span>
                  </div>
                )}

                {!isRecording && (
                  <div className="admin-chat-input-controls">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      title="Enviar Imagem"
                    >
                      📷
                    </Button>
                    <Button
                      type="button"
                      onClick={startRecording}
                      title="Gravar Áudio"
                    >
                      🎤
                    </Button>
                    <input
                      className="admin-chat-text-input"
                      placeholder="Digite uma mensagem..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      disabled={uploadingImage}
                    />
                    <Button
                      type="button"
                      onClick={handleSend}
                      disabled={!message.trim() || uploadingImage}
                    >
                      Enviar
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

